<template>
  <v-row align="center">
    <!-- Fundraiser Switch -->
    <v-col cols="12">
      <editor-switch-row label="Create Fundraiser">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <v-switch
              v-bind="props"
              v-model="campaignFundraisingEnabled"
              @update:modelValue="$emit('campaign-changed')"
              data-testid="ct-experience-fundraiser"
              density="compact"
              hide-details
              :disabled="
                !storefrontPricingEnabled ||
                disableFields ||
                $store.getters.getIsNewOrderAdded
              "
            />
          </template>
          <span v-if="disableFields || $store.getters.getIsNewOrderAdded">
            Fundraiser settings are locked after orders have been placed.
          </span>
          <span v-else-if="!storefrontPricingEnabled">
            Storefront pricing must be enabled to create fundraiser.
          </span>
          <span v-else>Create campaign fundraiser</span>
        </v-tooltip>
      </editor-switch-row>
    </v-col>

    <!-- divider -->
    <v-col
      class="feature-divider-flex"
      cols="12"
      v-if="campaignFundraisingEnabled"
    >
      <v-divider />
    </v-col>

    <!-- Select Fundraiser Type -->
    <v-col
      cols="12"
      v-if="campaignFundraisingEnabled && storefrontPricingEnabled"
      class="feature-drawer pa-4"
    >
      <v-row>
        <v-col cols="5">
          <v-radio-group
            v-model="fundraisingType"
            :disabled="disableFields || $store.getters.getIsNewOrderAdded"
            @update:modelValue="changeFundraisingType"
            required
            :rules="[validateNotEmpty]"
          >
            <v-radio
              value="flat_rate"
              label="Add a $ amount to each product"
            />
            <v-text-field
              v-if="fundraisingType == 'flat_rate'"
              label="Amount Charged per Item"
              prefix="$"
              class="mt-4"
              style="margin-left: 34px"
              type="number"
              step="0.01"
              :rules="[validateAllowance, validateMin(1)]"
              :disabled="
                disableFields || $store.getters.getIsNewOrderAdded
              "
              v-model="campaignFundraisingAmountPerItem"
              data-testid="ct-experience-fundraising-per-item"
              @update:modelValue="changeCampaignFundraisingAmountPerItem"
              @change="afterChangingCampaignFundraisingAmountPerItem"
              hint="Input dollar amount to add to each item"
            />
            <v-radio
              value="percentage"
              class="mt-4"
              label="Markup all products by a percentage"
            />
            <v-text-field
              v-if="fundraisingType == 'percentage'"
              label="Percent Charged per Item"
              class="mt-4"
              style="margin-left: 34px"
              prefix="%"
              type="number"
              :rules="[validatePercent, validateMin(1)]"
              :disabled="
                disableFields || $store.getters.getIsNewOrderAdded
              "
              v-model="campaignFundraisingAmountPerItem"
              data-testid="ct-experience-fundraising-per-item"
              @update:modelValue="changeCampaignFundraisingAmountPerItem"
              hint="Input percent amount to add to each item"
            />
          </v-radio-group>
        </v-col>
        <v-col cols="2" />
        <v-col cols="5">
          <div class="md-body-no-spacing mb-3" style="font-weight: 600">
            Set Fundraiser Goal
          </div>
          <v-text-field
            label="Set Fundraiser Goal (Required)"
            prefix="$"
            type="number"
            step="0.01"
            :rules="[validateAllowance, validateMin(1)]"
            :disabled="disableFields || $store.getters.getIsNewOrderAdded"
            v-model="campaignFundraisingGoal"
            data-testid="ct-experience-fundraising-goal"
            @update:modelValue="changeCampaignFundraisingGoal"
            @change="afterChangingCampaignFundraisingGoal"
            hint="Input dollar amount to add fundraiser goal"
          />
          <div
            class="d-flex align-center mt-3"
            style="margin-left: 0px"
          >
            <v-checkbox
              v-model="displayStorefrontFundraising"
              :disabled="
                !storefrontPricingEnabled ||
                disableFields ||
                $store.getters.getIsNewOrderAdded
              "
              @update:modelValue="$emit('campaign-changed')"
              data-testid="ct-experience-fundraiser-display"
              label="Do you want to display the fundraising bar on the storefront?"
            />
            <v-spacer />
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import helpers from "@/mixins/helpers";
import rules from "@/mixins/rules";
import EditorSwitchRow from "./components/EditorSwitchRow.vue";

export default {
  components: { EditorSwitchRow },

  name: "StoreFundraising",

  mixins: [rules, helpers],

  props: {
    campaign: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    disableFields: {
      type: Boolean,
      default: false,
    },
    storefrontPricingEnabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["campaign-changed"],

  data() {
    return {
      campaignFundraisingEnabled: false,
      campaignFundraisingAmountPerItem: 0,
      campaignFundraisingGoal: 0,
      fundraisingType: null,
      displayStorefrontFundraising: null,
    };
  },

  watch: {
    storefrontPricingEnabled(val) {
      if (!val) {
        this.campaignFundraisingEnabled = false;
      }
    },
  },

  methods: {
    changeFundraisingType() {
      this.$emit("campaign-changed");
    },

    changeCampaignFundraisingAmountPerItem(val) {
      const str = String(val);
      if (str.endsWith('.') || /\.\d*0$/.test(str)) return;
      if (val && this.isNumber(val)) {
        this.campaignFundraisingAmountPerItem = parseFloat(val);
        this.$emit("campaign-changed");
      }
    },

    afterChangingCampaignFundraisingAmountPerItem() {
      if (this.isNumber(this.campaignFundraisingAmountPerItem)) {
        this.campaignFundraisingAmountPerItem = this.formatCurrency(this.campaignFundraisingAmountPerItem);
      }
    },

    changeCampaignFundraisingGoal(val) {
      const str = String(val);
      if (str.endsWith('.') || /\.\d*0$/.test(str)) return;
      if (val && this.isNumber(val)) {
        this.campaignFundraisingGoal = parseFloat(val);
        this.$emit("campaign-changed");
      }
    },

    afterChangingCampaignFundraisingGoal() {
      if (this.isNumber(this.campaignFundraisingGoal)) {
        this.campaignFundraisingGoal = this.formatCurrency(this.campaignFundraisingGoal);
      }
    },

    /**
     * Returns fundraising data for saving the campaign.
     * @returns {Object} Fundraising fields to merge into campaign payload.
     */
    getData() {
      if (!this.campaignFundraisingEnabled) {
        return {
          fundraising_enabled: false,
          fundraising_amount_per_item: 0,
          fundraising_goal: 0,
          fundraising_type: this.fundraisingType,
          display_storefront_fundraising: this.displayStorefrontFundraising,
        };
      }
      return {
        fundraising_enabled: this.campaignFundraisingEnabled,
        fundraising_amount_per_item: this.campaignFundraisingAmountPerItem,
        fundraising_goal: this.campaignFundraisingGoal,
        fundraising_type: this.fundraisingType,
        display_storefront_fundraising: this.displayStorefrontFundraising,
      };
    },

    /**
     * Loads fundraising data from a campaign object.
     * @param {Object} campaign - The campaign to load from.
     */
    loadFromCampaign(campaign) {
      this.campaignFundraisingEnabled = campaign.fundraising_enabled;
      if (this.campaignFundraisingEnabled) {
        this.campaignFundraisingAmountPerItem =
          campaign.fundraising_amount_per_item;
        this.campaignFundraisingGoal = campaign.fundraising_goal;
        this.displayStorefrontFundraising =
          campaign.display_storefront_fundraising;
        this.fundraisingType = campaign.fundraising_type;
      }
    },
  },
};
</script>
